<template>
  <div class="question-bank-form">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1>{{ isEdit ? '编辑题库' : '创建题库' }}</h1>
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/admin/question-banks">题库管理</router-link>
          </li>
          <li class="breadcrumb-item active">{{ isEdit ? '编辑题库' : '创建题库' }}</li>
        </ol>
      </nav>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">加载中...</span>
      </div>
      <p class="mt-3 text-muted">正在加载题库信息...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <i class="bi bi-exclamation-triangle me-2"></i>
        {{ error }}
      </div>
      <button class="btn btn-outline-primary" @click="loadQuestionBank">
        <i class="bi bi-arrow-clockwise me-1"></i>
        重试
      </button>
    </div>

    <!-- 表单内容 -->
    <div v-else class="card">
      <div class="card-header">
        <h5 class="mb-0">{{ isEdit ? '编辑题库信息' : '创建新题库' }}</h5>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-md-6">
              <div class="mb-3">
                <label for="domain" class="form-label">
                  领域名称 <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  id="domain"
                  v-model="form.domain"
                  placeholder="请输入领域名称"
                  :class="{ 'is-invalid': errors.domain }"
                />
                <div v-if="errors.domain" class="invalid-feedback">
                  {{ errors.domain }}
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <label for="subdomain" class="form-label">
                  子领域名称 <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  id="subdomain"
                  v-model="form.subdomain"
                  placeholder="请输入子领域名称"
                  :class="{ 'is-invalid': errors.subdomain }"
                />
                <div v-if="errors.subdomain" class="invalid-feedback">
                  {{ errors.subdomain }}
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="mb-3">
                <label for="subdomainCode" class="form-label">
                  子领域代码 <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  id="subdomainCode"
                  v-model="form.subdomainCode"
                  placeholder="请输入子领域代码 (支持字母、数字和特殊字符)"
                  :class="{ 'is-invalid': errors.subdomainCode }"
                />
                <div v-if="errors.subdomainCode" class="invalid-feedback">
                  {{ errors.subdomainCode }}
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <label for="description" class="form-label">
                  题库描述
                </label>
                <textarea
                  class="form-control"
                  id="description"
                  v-model="form.description"
                  placeholder="请输入题库描述"
                  rows="3"
                ></textarea>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button
              type="button"
              class="btn btn-secondary"
              data-test="cancel-btn"
              @click="handleCancel"
            >
              取消
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="submitting"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
              {{ isEdit ? '更新' : '创建' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { questionBanksApi } from '@/api/questionBanks'
import type { QuestionBank } from '@/types'

const route = useRoute()
const router = useRouter()
const toast = useToast()

// 状态
const loading = ref(false)
const submitting = ref(false)
const error = ref('')
const questionBank = ref<QuestionBank | null>(null)

const form = ref({
  domain: '',
  subdomain: '',
  subdomainCode: '',
  description: ''
})

const errors = ref({
  domain: '',
  subdomain: '',
  subdomainCode: ''
})

// 计算属性
const isEdit = computed(() => !!route.params.id)

// 方法
const loadQuestionBank = async () => {
  const id = Number(route.params.id)
  if (!id) return

  loading.value = true
  error.value = ''

  try {
    const response = await questionBanksApi.getQuestionBank(id)
    questionBank.value = response
    form.value = {
      domain: response.domain,
      subdomain: response.subdomain,
      subdomainCode: response.subdomainCode ?? '',
      description: response.description || ''
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : '加载题库失败'
    toast.error('加载题库失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

const validateForm = () => {
  errors.value = {
    domain: '',
    subdomain: '',
    subdomainCode: ''
  }

  let isValid = true

  if (!form.value.domain.trim()) {
    errors.value.domain = '领域名称不能为空'
    isValid = false
  }

  if (!form.value.subdomain.trim()) {
    errors.value.subdomain = '子领域名称不能为空'
    isValid = false
  }

  if (!form.value.subdomainCode.trim()) {
    errors.value.subdomainCode = '子领域代码不能为空'
    isValid = false
  }

  return isValid
}

const handleSubmit = async () => {
  if (!validateForm()) return

  submitting.value = true

  try {
    if (isEdit.value) {
      const id = Number(route.params.id)
      await questionBanksApi.updateQuestionBank(id, form.value)
      toast.success('题库更新成功')
      router.push(`/admin/question-banks/${id}`)
    } else {
      const response = await questionBanksApi.createQuestionBank(form.value)
      toast.success('题库创建成功')
      router.push(`/admin/question-banks/${response.id}`)
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : '操作失败'
    error.value = message
    toast.error(message)
  } finally {
    submitting.value = false
  }
}

const handleCancel = () => {
  if (isEdit.value) {
    router.back()
  } else {
    router.push('/admin/question-banks')
  }
}

// 生命周期
onMounted(() => {
  if (isEdit.value) {
    loadQuestionBank()
  }
})
</script>

<style scoped>
.question-bank-form {
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.breadcrumb {
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .question-bank-form {
    padding: 1rem;
  }
}
</style>
